/* global google */
import Events from '@utilities/events'
import moduleInit from '@utilities/module-init';
import API from '@utilities/api'
import Form from '@utilities/form'
import GoogleMaps from '@utilities/google-maps'
import addAlertMessage from '@utilities/errors/add-alert-message'
import errorHandler from '@utilities/errors/error-handler'
import store from '@utilities/store'
import updateDom from '@utilities/update-dom';
import ScrollTo from "@utilities/scroll-to";
import queryString from 'query-string'
import debounce from 'lodash/debounce'
import storelocatorConstraints from './store-locator-constraints'

const JS_HOOK_STORE_LOCATOR_FORM = '[js-hook-store-locator-form]'
const JS_HOOK_STORE_LOCATOR_INPUT = '[js-hook-store-locator-input]'
const JS_HOOK_DETECT_LOCATION = '[js-hook-store-locator-detect-location]'
const JS_HOOK_LAT_INPUT = '[js-hook-store-locator-lat-input]'
const JS_HOOK_LONG_INPUT = '[js-hook-store-locator-long-input]'
const JS_HOOK_NEAREST_STORES_TEXT = 'js-hook-nearest-stores-text'
const JS_HOOK_LOCATION_NOT_FOUND = 'js-hook-location-not-found'
const JS_HOOK_CURRENT_LOCATION = 'js-hook-current-location'
const JS_HOOK_SHOW_MORE_STORES = 'js-hook-show-more-stores'
const JS_HOOK_SHOW_LESS_STORES = 'js-hook-show-less-stores'
const SHOW_MORE_BTN_VISIBLE = 'store__show-more--visible'
const SHOW_LESS_BTN_VISIBLE = 'store__show-less--visible'
const HIDDEN_NEAREST_STORES_TEXT = 'store-locator__result-nearest--hidden'
const HIDDEN_LOCATION_NOT_FOUND = 'store-locator__location-not-found--hidden'
const STORE_SELECT_ZOOM_LEVEL = 15
const JS_HOOK_STORE_LOCATOR_FILTER = '[js-hook-store-locator-filter]'
const JS_HOOK_STORE_LOCATOR_BAR = 'js-hook-store-locator-bar';
const JS_HOOK_STORE_LOCATOR_UPDATE = 'js-hook-store-locator-update';
const JS_HOOK_STORE_LOCATOR_FILTER_CONTEXT = '[js-hook-store-locator-filter-context]';
const JS_HOOK_PAGED_NAVIGATION = '[js-hook-paged-navigation]';
const JS_HOOK_SCROLL_LIST_MODAL = '[js-hook-store-locator-scroll-modal]';
const JS_HOOK_STORE_LOCATOR_CLEAR_FILTERS = '[js-hook-store-locator-filters-clear]'
const JS_HOOK_RADIUS_INPUT = '[js-hook-store-locator-radius-input]'
const JS_HOOK_RESET_BOUNDS_INPUT = '[js-hook-store-locator-reset-bounds-input]'

const MODAL_ID = 'refinement-modal';

class StoreLocatorForm extends Form {
  constructor(element) {
    super(element)

    // Form elements
    this.firstLoad = true
    this.formSelector = JS_HOOK_STORE_LOCATOR_FORM
    const { formType, locationNotFound,loadAsync, locale } = this.element.dataset || {}

    this.loadAsync = loadAsync
    this.formType = formType
    this.locationNotFound = locationNotFound
    this.locale = locale
    this.nearestStoresText = document.querySelector(`[${JS_HOOK_NEAREST_STORES_TEXT}]`)
    this.showMoreButton = document.querySelector(`[${JS_HOOK_SHOW_MORE_STORES}]`)
    this.showLessButton = document.querySelector(`[${JS_HOOK_SHOW_LESS_STORES}]`)
    this.locationNotFound = document.querySelector(`[${JS_HOOK_LOCATION_NOT_FOUND}]`)
    this.storeLocatorFiltersContext = document.querySelector(JS_HOOK_STORE_LOCATOR_FILTER_CONTEXT);
    this.url = '';
    this.storeLocatorUpdates = [...document.querySelectorAll(`[${JS_HOOK_STORE_LOCATOR_UPDATE}]`)]
    this.pagedNavigationId = document.querySelector(JS_HOOK_PAGED_NAVIGATION)?.dataset.id

    this.selectStorelocatorFilters()

    this.ui = {
      input: this.form.querySelector(JS_HOOK_STORE_LOCATOR_INPUT),
      latInput: this.form.querySelector(JS_HOOK_LAT_INPUT),
      longInput: this.form.querySelector(JS_HOOK_LONG_INPUT),
      radiusInput: this.form.querySelector(JS_HOOK_RADIUS_INPUT),
      resetBoundsInput: this.form.querySelector(JS_HOOK_RESET_BOUNDS_INPUT),
      detectLocation: this.form.querySelector(JS_HOOK_DETECT_LOCATION),
    }

    this.debouncedOnModalOpen = debounce(() => this.onModalOpen())

    this.initConstraints()

    // Events
    this.bindSubmitEvents()
    this.bindChangeEvents()
    this.bindEvents()

    if (!this.loadAsync) this.submitForm()
  }

  /**
   * Update the DOM of chips
   * @param {HTMLElement} content - DOM element
   */
  static updateModal(content) {
    updateDom(content, JS_HOOK_STORE_LOCATOR_BAR);
  }

  static updateInteractiveElements(content) {
    const interactiveElements = {
      [JS_HOOK_STORE_LOCATOR_BAR]: StoreLocatorForm.updateModal,
    };

    Object.keys(interactiveElements).forEach((selector) => {
      interactiveElements[selector](content);
    });
  }

  rebindEvents() {
    // Bind All Events
    Events.$trigger('events::dom-reinit');

    this.selectStorelocatorFilters();
    this.bindEvents();
  }

  initModules(scrollTop = null, scrollList = null) {
    moduleInit.async('[js-hook-dropdown]', () =>
      import(/* webpackChunkName: "js/Dropdown" */ '@components/dropdown'),
    );

    moduleInit.async('[js-hook-store-locator-filter]', () =>
      import(/* webpackChunkName: "js/store-locator-filters" */ '@components/store-locator-filter'),
    );

    moduleInit.async('[js-hook-paged-navigation]',
      () =>
        import(/* webpackChunkName: "js/PagedNavigation" */ '@components/paged-navigation'),
      {
        viewID: this.pagedNavigationViewID,
        scrollTop,
        scrollList
      }
    );

    moduleInit.async('[js-hook-sort-select-box]', () =>
      import(/* webpackChunkName: "js/SortSelectBox" */ '@components/sort-select-box'),
    );

    moduleInit.async('[js-hook-custom-select-box]', () =>
      import(/* webpackChunkName: "js/CustomSelectBox" */ '@components/custom-select-box'),
    );

    moduleInit.async('[js-hook-pagination]', () =>
      import(/* webpackChunkName: "js/Pagination" */ '@components/pagination'),
    );

    // Bind Modal Events
    Events.$trigger('modal::bind-buttons', { data: { id: MODAL_ID } });
  }

  /**
   * Replace static DOM elements
   *
   * @param {Object} content - DOM element
   */
  updateStaticElements(content) {
    this.storeLocatorUpdates.forEach((selector) => {
      const hookName = selector.getAttribute(JS_HOOK_STORE_LOCATOR_UPDATE);

      setTimeout (() => {
        updateDom(content, `[${JS_HOOK_STORE_LOCATOR_UPDATE}=${hookName}]`);
      }, 0); // don't ask, weird fix for weird safari iphone bug
    });
  }

  /**
   * Parse Axios results and updated select DOM elements
   *
   * @param {string} response - Axios response HTML code
   */
  getDataFromFilterSuccess(response) {
    Events.$trigger('loader::hide');

    // Parse using DOMParser
    const content = new DOMParser().parseFromString(response, 'text/html').body;

    // Update static DOM elements that do not have interaction
    this.updateStaticElements(content);

    // Update interactive DOM elements
    StoreLocatorForm.updateInteractiveElements(content);

    setTimeout (() => {
      // Scroll if needed
      if (this.filterId && (this.scrollTop !== null)) {
        const filterList = document.getElementById(this.filterId);
        const filterScrollListModal = filterList.querySelector(JS_HOOK_SCROLL_LIST_MODAL);

        this.initModules(this.scrollTop, filterScrollListModal);
      } else {
        this.initModules();
      }

      //   Bind events again to new DOM elements
      this.rebindEvents();
    }, 1); // don't ask, weird fix for weird safari iphone bug

    if (this.scrollTop) {
      ScrollTo.scrollTo(this.storeLocatorFiltersContext, 500, 150);
    }
  }

  /**
   * Retrieve data from filter
   * @param {Object} data - { url, params } for API
   * @param pushState boolean - Determines if history is replaced or pushed
   */
  getDataFromFilter(data, pushState) {
    if (!data) return;
    Events.$trigger('loader::show');

    this.url = data.url;

    const { query, url: newUrl } = queryString.parseUrl(this.url);
    const bodyObj = {
      selectedUrl: this.url,
      ...query,
    };

    this.filterId = data.filterId;

    this.scrollTop = data.scrollTop;

    API.get(newUrl, bodyObj)
      .then((response) => this.getDataFromFilterSuccess(response.data, pushState))
      .catch(() => Events.$trigger('loader::hide'));
  }

  /**
   * success handler from get current location
   * @param {Object} position - lat long
   */
  getCurrentLocationSuccess(position) {
    const { latitude: lat, longitude: lng } = position.coords || {}
    this.resetInput()
    this.setGeolocation({ lat, lng })
    GoogleMaps.utilities.setZoom(STORE_SELECT_ZOOM_LEVEL)
  }

  /**
   * error handler from get current location
   * @param {Boolean} notSupported - if browser support geolocation
   */
  getCurrentLocationError(notSupported) {
    this.hideLoader()
    // Geolocation IS NOT available
    const notSupportedMessage = notSupported ? ` ${this.element.dataset.errorNotSupported}` : ''
    const notFoundMessage = this.element.dataset?.errorNotFound || 'Error'

    addAlertMessage({
      message: `${notFoundMessage}${notSupportedMessage}`,
      type: 'warning',
    })
  }

  /**
   * get current geolocation
   */
  getCurrentLocation() {
    Events.$trigger('loader::show')

    if ('geolocation' in navigator) {
      return navigator.geolocation.getCurrentPosition(
        (position) => this.getCurrentLocationSuccess(position),
        () => this.getCurrentLocationError(),
      )
    }

    return this.getCurrentLocationError(true)
  }

  hideLoader() {
    Events.$trigger('loader::hide')
    if (this.ui.detectLocation) this.ui.detectLocation.classList.remove('is--loading')
  }

  lookupAddressSuccess({ status, location }) {
    if (status === google.maps.GeocoderStatus.OK) {
      this.setGeolocation({ lat: location.lat(), lng: location.lng() })
      if (this.locationNotFound) {
        this.locationNotFound.classList.add(HIDDEN_LOCATION_NOT_FOUND)
      }
    } else if (status === google.maps.GeocoderStatus.ZERO_RESULTS) {
      if (this.locationNotFound) {
        this.locationNotFound.classList.remove(HIDDEN_LOCATION_NOT_FOUND)
      }
      this.hideButtonLoaders()
      Events.$trigger('loader::hide')
    } else {
      this.hideButtonLoaders()
      Events.$trigger('loader::hide')
    }
  }

  setGeolocation({ lat, lng }) {
    this.ui.latInput.value = lat || 0
    this.ui.longInput.value = lng || 0

    store.dispatch('setLocation', { location: { lat, lng } })
    this.submitForm()
  }

  reset() {
    this.resetInput()
    this.submitForm(true)
  }

  resetInput() {
    this.ui.input.value = ''
    this.ui.latInput.value = ''
    this.ui.longInput.value = ''
  }

  getFilterResults(filter) {
    return API.get(filter.dataset.url)
      .then((response) => this.setResults(response))
      .catch((error) => this.submitFormError(error))
  }

  clearFilters(filter) {
    const url = filter.href

    Events.$trigger('history::replace', {
      data: { url: `${url}` },
    })

    Events.$trigger('history::push', {
      data: {
        url: `${url}`,
        state: {
          url: `${url}`
        }
      }
    });

    Events.$trigger('history::storelocator-update', {
      data: {
        url: `${url}`,
        state: {
          url: `${url}`,
          target: 'store-locator',
        }
      },
    })

    return API.get(filter.dataset.url)
      .then((response) => this.setResults(response))
      .catch((error) => this.submitFormError(error))
  }

  selectStorelocatorFilters() {
    this.storeLocatorFilters = [...document.querySelectorAll(JS_HOOK_STORE_LOCATOR_FILTER)]
    this.storeLocatorFiltersClear = document.querySelector(JS_HOOK_STORE_LOCATOR_CLEAR_FILTERS)
  }

  setResults({ data }) {
    store.dispatchWithPromise('setStores', { stores: data.stores }).then(() => {
      /**
       * Check for message "Nearest 5 stores to ${value}", if its more then 4
       * it will remove hidden class that have display: none; and update text.
       * Additional check for button's hide/show more.
       */
      const storesFound = data.stores.length;
      this.showLessButton.classList.remove(SHOW_LESS_BTN_VISIBLE);
      this.showMoreButton.classList.remove(SHOW_MORE_BTN_VISIBLE);
      if (storesFound > 4) {
        this.nearestStoresText.textContent = `Nearest 5 stores to  ${this.ui.input.value}.`;
        this.nearestStoresText.classList.remove(HIDDEN_NEAREST_STORES_TEXT);
        this.showMoreButton.classList.add(SHOW_MORE_BTN_VISIBLE);
      } else {
        this.nearestStoresText.classList.add(HIDDEN_NEAREST_STORES_TEXT);
      }

      this.hideButtonLoaders();
      Events.$trigger('loader::hide');
    // eslint-disable-next-line no-unused-vars
    }).catch((_error) => {
      // use this for debugging purposes. The catch is supposed to never be called.
      // console.log(_error)
      Events.$trigger('loader::hide');
    });
  }

  addressAutocomplete () {
    setTimeout(() => {
      const inputField = document.querySelector(JS_HOOK_STORE_LOCATOR_INPUT);
      const autocomplete = new google.maps.places.Autocomplete(inputField, {
        fields: ["place_id", "geometry", "name"],
        types: ['geocode'],
      });

      autocomplete.addListener("place_changed", () => {
        const place = autocomplete.getPlace();

        if (place.geometry) {
          this.preSubmitForm();
        }
      });
    }, 1000);
  }

  /**
   * Look up adress before submit
   */
  preSubmitForm() {
    const { searchParam } = store.state

    Events.$trigger('alert::close')
    Events.$trigger('loader::show')

    if (this.ui.input.value === '' && this.ui.input.value !== searchParam) {
      return this.submitForm()
    }

    return GoogleMaps.utilities.lookupAddress(this.ui.input.value, (data) =>
      this.lookupAddressSuccess(data),
    )
  }

  /**
   * Submit form to BE
   */
  submitForm() {
    if (this.formType && this.formType === 'landing') {
      return this.form.submit()
    }

    const params = {
      lat: this.ui.latInput?.value || '',
      long: this.ui.longInput?.value || '',
      postalCode: this.ui.input?.value || '',
      radius: this.ui.radiusInput?.value || '',
    }

    store.dispatch('setSearchParam', {
      searchParam: this.ui.input?.value || '',
      location: { lat: params.lat, lng: params.long },
    })

    Events.$trigger('history::replace', {
      data: { url: `${window.location.pathname}?${queryString.stringify(params)}` },
    })

    Events.$trigger('history::push', {
      data: {
        url: `${window.location.pathname}?${queryString.stringify(params)}`,
        state: {
          url: `${window.location.pathname}?${queryString.stringify(params)}`
        }
      }
    });

    Events.$trigger('history::storelocator-update', {
      data: {
        url: `${window.location.pathname}?${queryString.stringify(params)}`,
        state: {
          url: `${window.location.pathname}?${queryString.stringify(params)}`,
          target: 'store-locator',
        }
      },
    })

    return API.get(this.endpoint, params)
      .then((response) => this.setResults(response))
      .catch((error) => this.submitFormError(error))
  }

  onModalOpen() {
    if (this.firstLoad) this.submitForm()
    this.firstLoad = false

    Events.$trigger('shipping-form::resetStore')
  }

  submitFormError(error) {
    this.hideButtonLoaders()
    errorHandler(error)
  }

  bindEvents() {
    if (this.loadAsync) Events.$on('store-locator-modal::open', () => this.debouncedOnModalOpen())

    Events.$on('store-locator-modal::input-change', () => {
      Events.$trigger('modal::open', { data: { id: 'click-and-collect-modal' } })
      if (this.loadAsync) this.debouncedOnModalOpen()
    })

    this.currentLocation = document.querySelector(`[${JS_HOOK_CURRENT_LOCATION}]`)

    if(this.currentLocation) {
      this.currentLocation.addEventListener(
        'click',
        () => {
          this.getCurrentLocation()
        },
        false,
      )
    }

    Events.$on(`pagedNavigation[${this.pagedNavigationId}]::changedView`, (e, data) => {
      this.pagedNavigationViewID = data.viewID;
    });

    Events.$on(`modal[${MODAL_ID}]::open`, () => {
      Events.$trigger(`pagedNavigation[${this.pagedNavigationId}]::changeView`, {
        data: {
          viewID: 'refinement-landing',
          animate: false
        }
      });
    });

    Events.$on('history::storelocator-update', (_, data) => {
      const url = data?.state?.url || window.location.href
      const scrollTop = data?.state?.scrollTop;
      const filterId = data?.state?.filterId;

      this.getDataFromFilter({url, scrollTop, filterId}, false)
    })

    Events.$on('storelocator:zoom', (e, data) => {
      if (data.triggeredByButtons) {
        this.loadNewStoresOnMapChange(data);
      }
    });

    Events.$on('storelocator:bounds_changed', (e, data) => {
      this.loadNewStoresOnMapChange(data);
    });

    this.initFilterEvent();
    this.addressAutocomplete();

    if(this.storeLocatorFiltersClear) {
      this.storeLocatorFiltersClear.addEventListener('click', (e) => {
        e.preventDefault();
        this.clearFilters(this.storeLocatorFiltersClear);
      })
    }
  }

  initFilterEvent() {
    if(this.storeLocatorFilters) {
      this.storeLocatorFilters.forEach(filter => {
        filter.addEventListener('click', (e) => {
          e.preventDefault();
          this.getFilterResults(filter);
        })
      })
    }
  }

  loadNewStoresOnMapChange(data) {
      const thisForm = document.querySelector(JS_HOOK_STORE_LOCATOR_FORM);
      const hasMapEvents = thisForm?.dataset?.mapEvents === 'true';
      if (data && hasMapEvents) {
        Events.$trigger('loader::show');
        const { lat, lng, radius } = data;
        const { resetBoundsInput } = this.ui;
        resetBoundsInput.value = false;

        const params = {
          lat: lat || '',
          long: lng || '',
          postalCode: '',
          radius: radius || '',
        }

        return API.get(this.endpoint, params)
          .then((response) => {
            this.setResults(response);
            resetBoundsInput.value = true;
          })
          .catch((error) => {
            this.submitFormError(error);
            resetBoundsInput.value = true;
          })
      }

      return false;
  }

  /**
   * Init constraints for form validation for validate.js
   */
  initConstraints() {
    this.inputs = [...this.form.querySelectorAll(this.inputTypes)]
    this.constraints = storelocatorConstraints
  }
}

export default StoreLocatorForm
